import {AfterContentChecked, Component, OnInit, ViewChild} from '@angular/core';
import {SharepointService} from 'app/services/sharepoint.service';
import {CompetitionBackgroundComponent} from 'app/shared/components/competition-background/competition-background.component';
import {CompetitionLogoComponent} from 'app/shared/components/competition-logo/competition-logo.component';
import { zip, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {DropdownMenuOption} from 'app/models/dropdown-menu-option';
import {CompetitionService} from 'app/services/competition.service';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {Competition} from 'app/models/competition';
import {DialogHelperService} from '../../services/dialog-helper.service';
import slugify from 'slugify';

@Component({
    selector: 'app-admin',
    templateUrl: './admin.component.html',
    styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit {

    options: Array<DropdownMenuOption>;
    selected: DropdownMenuOption;
    competition: Competition;
    editMode: boolean = false;
    newCompetitionName: string;
    newCompetitionUrl: string;
    newCompetitionCustomTitle: string;
    @ViewChild('bgcompetition') bgComponent: CompetitionBackgroundComponent;
    @ViewChild('ftcompetition') ftComponent: CompetitionBackgroundComponent;
    @ViewChild('lgcompetition') lgComponent: CompetitionLogoComponent;

    constructor(
        private competitionService: CompetitionService,
        private sharepointService: SharepointService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private dialogHelper: DialogHelperService,
    ) {}

    ngOnInit() {
        this.competition = this.competitionService.getCompetitionSelected();
        this.getCompetitions();
    }

  onSelectCompetition(event): void {
      if (event.id === this.competition.compUrl) {
        return;
      }
      const competition = this.competitionService.getCompetitionSelectedByUrl(event.id);
      this.competitionService.setCompetitionSelected(competition);
      this.competition = competition;
      this.router.navigate(['competition', event.id, 'onboarding'],
          {relativeTo: this.activatedRoute.parent});
      this.selected = event;
    }

    private getCompetitions(): void {
        const competitions = this.competitionService.getCompetitions();

        if (!this.competition && !!competitions.length) {
            this.competitionService.setCompetitionSelected(competitions[0]);
        } else if (!this.competition) {
            return;
        }

        this.options = this.competitionService.getDropdownMenuOptions();
        this.selected = this.options.filter((option) =>
            option.id === this.competitionService.getCompetitionUrl())[0];
        this.redirectToCompetitionId();
    }

    private redirectToCompetitionId(): void {
        if (this.activatedRoute) {
            this.activatedRoute.params.subscribe((params: Params) => {
                if (!params.id) {
                    this.router.navigate(['competition', this.selected.id, 'onboarding'],
                        {relativeTo: this.activatedRoute.parent});
                }
            });
        }
    }
    onEdit() {
        this.editMode = true;
        this.newCompetitionCustomTitle = this.competition.custom_title !== undefined ? this.competition.custom_title : '';
    }
    onAdd() {}
    onCancel() {
        this.newCompetitionName = undefined;
        this.newCompetitionUrl = undefined;
        this.newCompetitionCustomTitle = undefined;
        this.editMode = false;
        if (this.bgComponent) this.bgComponent.cancel();
        if (this.ftComponent) this.ftComponent.cancel();
        if (this.lgComponent) this.lgComponent.cancel();
        // Reset current competition state by reloading it from the service/API
        const compUrlForReload = this.competition.compUrl;
        this.competitionService.get(compUrlForReload).subscribe(comp => {
            this.competitionService.setCompetitionSelected(comp);
            this.competition = comp;
        });
    }
    onPreview() {
        this.router.navigate([this.competitionService.getCompetitionUrl()]);
    }

    onNewCompetition() {
        this.router.navigate(['competition'], {relativeTo: this.activatedRoute.parent});
    }
    onDeleteCompetition() {
      const confirmed = window.confirm(`Are you sure you want to delete this competition?`);
      if (!confirmed) return;

        this.competition = this.competitionService.getCompetitionSelected();
        this.competitionService.deleteCompetition(this.competition.id).subscribe(r => {
            this.competitionService.setCompetitionSelected(undefined);
            this.router.navigateByUrl('competition')
        })
    }

    urlChanged() {
      this.newCompetitionUrl = slugify(this.newCompetitionUrl);
    }

    onFileError(size: number) {
      this.dialogHelper.showPrompt(
        'File too big error',
        'Uploaded file is too big. Choose a file below 400 KB. Your file was: '
        + size.toString()
        + ' KB',
        'OK',
        () => {}
      );
    }

    onSaveEdit() {
      const oldUrl = this.competition.compUrl;

      if (this.newCompetitionName && this.newCompetitionName !== this.competition.displayName) {
        this.competition.displayName = this.newCompetitionName;
      }
      if (this.newCompetitionUrl && this.newCompetitionUrl !== this.competition.compUrl) {
        this.competition.compUrl = slugify(this.newCompetitionUrl);
      }
      this.competition.custom_title = this.newCompetitionCustomTitle !== undefined ? this.newCompetitionCustomTitle.trim() : (this.competition.custom_title ?? '');

      const backgroundBlob = this.competition.backgroundUrl;
      const footerImageBlob = this.competition.footerImageUrl;
      const competitionToSave = {...this.competition};
      delete competitionToSave.backgroundUrl;
      delete competitionToSave.footerImageUrl;
      delete competitionToSave.assetFooterImage;
      if (footerImageBlob) {
          competitionToSave.has_footer_image = true;
      }

      this.competitionService.update(competitionToSave as Competition).subscribe(r => {
        const uploads: Array<{ blob: Blob, asset: any, label: string }> = [
          { blob: backgroundBlob, asset: r.assetBackground, label: 'background image' },
          { blob: footerImageBlob, asset: r.assetFooterImage, label: 'footer image' },
        ].filter(upload => upload.blob && upload.asset);

        if (!uploads.length) {
          this.finalizeSave(r, oldUrl);
          return;
        }
        zip(...uploads.map(upload =>
          this.sharepointService.upSharepointFile(Object.assign(upload.asset, {
            mimeType: 'image/*',
          }), upload.blob).pipe(catchError(() => {
            this.dialogHelper.showPrompt('Upload error', `Unable to upload ${upload.label}.`, 'OK', () => {});
            return of(null);
          }))
        )).subscribe(() => this.finalizeSave(r, oldUrl));
      }, error => {
         this.newCompetitionUrl = undefined;
         if (!!!error.handled) {
           this.dialogHelper.showPrompt(
             'Unable to update competition',
             typeof error.error === 'object' ? error.statusText : error.error,
             'OK',
             () => {}
           );
         }
      });
    }

    private finalizeSave(r: Competition, oldUrl: string) {
      this.competition = r;
      // Warning: id in options is compUrl param from model
      const opt = this.options.filter((option) => option.id === oldUrl)[0];
      if (opt) {
        opt.name = r.displayName;
      }
      this.selected.name = r.displayName;
      if (oldUrl !== r.compUrl) {
        this.selected.id = r.compUrl;
      }
      this.competitionService.setCompetitionSelected(r);
      this.newCompetitionName = undefined;
      this.newCompetitionUrl = undefined;
      this.newCompetitionCustomTitle = undefined;
      this.editMode = false;
      if (oldUrl !== r.compUrl) {
        this.competitionService.getAll(this.selected.id).subscribe(comps => {
          this.competitionService.setCompetitions(comps);
          this.options = this.competitionService.getDropdownMenuOptions();
        });
        this.router.navigate(['competition', r.compUrl, 'onboarding'],
          {relativeTo: this.activatedRoute.parent});
      }
    }

}
